import React, { useEffect, useMemo, useState } from "react";
import { jsonGet } from "../utils/http";
import { useNavigate } from "react-router-dom";
import { fetchCurrentAthleteProfile, fetchSports, groupSportsByCategory, Sport } from "../utils/athleteApi";
import { isAthlete } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import DeleteAthleteModal from "./DeleteAthleteModal";
import ComboBox, { ComboBoxOptionGroup } from "./ComboBox";
import { useAuth } from "../contexts/AuthContext";
import { CompanyAthleteResponse } from "../types/athletes";
import "../styles/forms.css";
import "../styles/profile-form.css";

export type AthleteSaveMeta = {
    companyAthlete?: CompanyAthleteResponse;
};

function isCompanyAthleteResponseBody(data: unknown): data is CompanyAthleteResponse {
    if (!data || typeof data !== "object") return false;
    const o = data as Record<string, unknown>;
    return typeof o.userId === "number" && typeof o.athleteProfileId === "number";
}

export type AthleteProfile = {
    id?: string | number;           // ← allow id when editing
    firstName: string;
    lastName: string;
    email: string;
    parentEmail: string;            // ← now required
    parentEmail2?: string;
    state: string;
    stateId?: number;
    staffName: string;
    staffId?: number;
    // City support
    cityId?: number;
    cityName?: string;
    nickname?: string;
    graduationYear?: number;
    school?: string;
    program?: string;
    location?: string;
    pronouns?: string;
    dateOfBirth?: string;
    athletePhone?: string;
    parentPhone?: string;
    instagramHandle?: string;
    tiktokHandle?: string;
    linkedinUrl?: string;
    facebookName?: string;
    hudlUrl?: string;
    sport?: Sport;
    sportName?: string;
};

export type AddAthleteProfileFormProps = {
    loadUrl: string;                 // GET returns { states, staff, ... }
    saveUrl: string;                 // POST /api/athletes or PUT /api/athletes/:id
    useCookies?: boolean;
    onSaved?: (saved: AthleteProfile, meta?: AthleteSaveMeta) => void;
    onClose?: () => void;
    afterSaveTo?: string | ((saved: AthleteProfile) => string);
    /** NEW: prefill when navigating from a card */
    initial?: Partial<AthleteProfile>;
};

type StateOption = { id: number; name: string };
type StaffOption = { id: number; name: string };
type ViewPayload = {
    states?: StateOption[];
    staff?: StaffOption[];
    // profileTypes?: { id: number; name: string }[]  // not used for athlete creation
};

function normalizeStaffId(id: unknown): number | undefined {
    if (id == null || id === "") return undefined;
    const parsed = typeof id === "number" ? id : Number(id);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function findStaffOption(
    staffOpts: StaffOption[],
    staffId: unknown,
    staffName?: string,
): StaffOption | undefined {
    const normalizedId = normalizeStaffId(staffId);
    if (normalizedId != null) {
        const byId = staffOpts.find((o) => o.id === normalizedId);
        if (byId) return byId;
    }
    if (staffName) {
        return staffOpts.find((o) => o.name.toLowerCase() === staffName.toLowerCase());
    }
    return undefined;
}

export default function AddAthleteProfileForm({
    loadUrl,
    saveUrl,
    useCookies = false,
    onSaved,
    onClose,
    afterSaveTo,
    initial,
}: AddAthleteProfileFormProps) {
    const navigate = useNavigate();
    const { authProvider, userInfo } = useAuth();
    const isCleverUser = authProvider === "clever";

    const [data, setData] = useState<AthleteProfile>({
        id: initial?.id,
        firstName: initial?.firstName || "",
        lastName: initial?.lastName || "",
        email: initial?.email || "",
        parentEmail: initial?.parentEmail || "",
        parentEmail2: initial?.parentEmail2 || "",
        state: initial?.state || "",
        stateId: initial?.stateId,
        staffName: initial?.staffName || "",
        staffId: initial?.staffId,
        cityId: initial?.cityId,
        cityName: initial?.cityName,
        sport: initial?.sport,
        sportName: initial?.sportName,
    });

    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [sports, setSports] = useState<Sport[]>([]);
    const [sportsLoading, setSportsLoading] = useState(true);
    const [sportsError, setSportsError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AthleteProfile | "city", string>>>({});
    const [ok, setOk] = useState<string>("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    // Lock only when an existing athlete already had a guardian email at open time.
    // Do not key off live `data.parentEmail` — typing the first character would disable the field.
    const [parentEmailLocked] = useState(
        () => !!initial?.id && Boolean((initial?.parentEmail ?? "").trim()),
    );
    const [parentEmail2Locked] = useState(
        () => !!initial?.id && Boolean((initial?.parentEmail2 ?? "").trim()),
    );

    // Load athlete profile data from /api/athletes/me if user is an athlete
    useEffect(() => {
        if (isAthlete() && !initial?.id) {
            (async () => {
                try {
                    const athleteProfile = await fetchCurrentAthleteProfile();
                    if (athleteProfile) {
                        console.log('Athlete profile loaded from API:', athleteProfile);
                        setData((prev) => ({
                            ...prev,
                            id: athleteProfile.id,
                            firstName: athleteProfile.firstName || prev.firstName,
                            lastName: athleteProfile.lastName || prev.lastName,
                            email: athleteProfile.email || prev.email,
                            state: athleteProfile.stateName || prev.state,
                            staffName: athleteProfile.staffName || prev.staffName,
                        }));
                    }
                } catch (error) {
                    console.error('Failed to load athlete profile:', error);
                }
            })();
        }
    }, [initial?.id]);

    // Load dropdown options, then reconcile with any initial values
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setServerError("");
            try {
                const view = await jsonGet<ViewPayload>(loadUrl, useCookies);
                const staffOpts: StaffOption[] = Array.isArray(view?.staff) ? view!.staff! : [];

                if (cancelled) return;

                setStaffOptions(staffOpts);

                // Seed defaults only when not already provided by initial/state
                setData((d) => {
                    let next = { ...d };


                    if (staffOpts.length > 0) {
                        const isEdit = d.id != null;
                        const match = findStaffOption(staffOpts, d.staffId, d.staffName);
                        if (match) {
                            next.staffName = match.name;
                            next.staffId = match.id;
                        } else if (!isEdit) {
                            next.staffName = staffOpts[0].name;
                            next.staffId = staffOpts[0].id;
                        }
                    }

                    return next;
                });
            } catch (e: any) {
                if (!cancelled) setServerError(e?.message || "Failed to load form options.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [loadUrl, useCookies]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setSportsLoading(true);
            setSportsError(null);
            try {
                const catalog = await fetchSports();
                if (cancelled) return;
                const list = Array.isArray(catalog) ? catalog : [];
                setSports(list);
                if (!catalog) {
                    setSportsError("Failed to load sports");
                }
                setData((d) => {
                    if (d.sport?.id) {
                        const match = list.find((sport) => sport.id === d.sport?.id);
                        return match ? { ...d, sport: match, sportName: match.name } : d;
                    }
                    const sportName = (d.sportName || "").trim();
                    if (!sportName) return d;
                    const match = list.find((sport) => sport.name.toLowerCase() === sportName.toLowerCase());
                    return match ? { ...d, sport: match, sportName: match.name } : d;
                });
            } catch {
                if (!cancelled) {
                    setSports([]);
                    setSportsError("Failed to load sports");
                }
            } finally {
                if (!cancelled) setSportsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const sportGroups: ComboBoxOptionGroup<Sport>[] = useMemo(
        () => groupSportsByCategory(sports).map((group) => ({
            label: group.category,
            options: group.sports,
        })),
        [sports]
    );

    // City fetching logic removed - handled by CityComboBox

    const set = (k: keyof AthleteProfile) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
            setData((s) => ({ ...s, [k]: e.target.value }));

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email || "");
    const parentEmailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.parentEmail || "");
    const parentEmail2Value = (data.parentEmail2 || "").trim();
    const parentEmail2Ok = !parentEmail2Value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail2Value);

    async function handleDeleteConfirm() {
        if (!data.id) return;

        setSaving(true);
        setServerError("");

        try {
            // Construct delete URL: DELETE /api/athletes/{athleteProfileId}
            const deleteUrl = buildApiUrl(`api/athletes/${data.id}`);

            const res = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                    Accept: "application/json",
                },
                credentials: "include", // Send HTTP-only cookies
            });

            if (!res.ok) {
                throw new Error(`Failed to delete athlete: HTTP ${res.status}`);
            }

            setOk("Athlete deleted successfully.");

            // Notify parent component about the deletion
            onSaved?.(data);

            // Close the delete modal
            setShowDeleteModal(false);

            // Close the modal after successful deletion
            setTimeout(() => {
                onClose?.();
                // Navigate if afterSaveTo is provided
                if (afterSaveTo) {
                    const target = typeof afterSaveTo === "function" ? afterSaveTo(data) : afterSaveTo;
                    if (target) {
                        navigate(target);
                    }
                }
            }, 500);
        } catch (e: any) {
            setServerError(e?.message || "Failed to delete athlete.");
            setShowDeleteModal(false);
        } finally {
            setSaving(false);
        }
    }

    async function onSave() {
        setServerError("");
        setOk("");

        const errors: Partial<Record<keyof AthleteProfile | "city", string>> = {};

        if (!data.firstName.trim()) {
            errors.firstName = "First name is required.";
        }
        if (!data.lastName.trim()) {
            errors.lastName = "Last name is required.";
        }
        if (!emailOk) {
            errors.email = "Please enter a valid email.";
        }
        if (!isCleverUser && !parentEmailOk) {
            errors.parentEmail = "Please enter a valid parent/guardian email.";
        }
        if (!isCleverUser && parentEmail2Value) {
            if (!parentEmail2Ok) {
                errors.parentEmail2 = "Please enter a valid parent/guardian email.";
            } else if (parentEmail2Value.toLowerCase() === (data.parentEmail || "").trim().toLowerCase()) {
                errors.parentEmail2 = "Parent/guardian emails must be different.";
            } else if (parentEmail2Value.toLowerCase() === (data.email || "").trim().toLowerCase()) {
                errors.parentEmail2 = "Parent/guardian email must be different from the athlete email.";
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setFieldErrors({});

        // If creating a new athlete (not editing), check subscription limits
        if (!data.id) {
            try {
                // Fetch fresh limits data right before creating the athlete
                const { fetchSubscriptionLimits, isAtAthleteLimit } = await import('../services/subscriptionLimitsService');
                const freshLimits = await fetchSubscriptionLimits();

                // Check if user has reached their limit (unlimited plans, incl. the -1
                // sentinel, are never at the limit).
                if (isAtAthleteLimit(freshLimits)) {
                    setServerError(`You have reached your subscription's athlete limit (${freshLimits.maxAthletes} ${freshLimits.maxAthletes === 1 ? 'athlete' : 'athletes'}). Please upgrade your plan to add more athletes.`);
                    return;
                }
            } catch (limitsError: any) {
                // If limits check fails, continue and let backend enforce the limit with 422 response
                // The backend will return a 422 error if the limit is exceeded
            }
        }


        // Ensure staff is consistent with the selected option
        if (staffOptions.length > 0) {
            const match =
                findStaffOption(staffOptions, data.staffId, data.staffName) ||
                (data.id == null ? staffOptions[0] : undefined);
            if (match) {
                data.staffName = match.name;
                data.staffId = match.id;
            }
        }

        setSaving(true);
        try {
            // Determine if this is create or update
            const isUpdate = data.id != null;
            // Prefer the in-memory numeric id; fall back to localStorage only if AuthContext isn't populated yet.
            const parentUserId =
                userInfo?.id != null ? String(userInfo.id) : localStorage.getItem("userId");

            // Construct the correct URL and method
            let url = saveUrl;
            let method: "POST" | "PUT" = "POST";

            if (isUpdate) {
                // UPDATE: Use PUT with email and parentUserId in URL
                if (!data.email || !parentUserId) {
                    setServerError("Email and user ID are required for updates.");
                    setSaving(false);
                    return;
                }
                url = buildApiUrl(`api/athletes/${encodeURIComponent(data.email)}/${parentUserId}`);
                method = "PUT";
            }

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                credentials: "include", // Send HTTP-only cookies
                body: JSON.stringify(data),
            });

            // Handle error responses with user-friendly messages
            if (!res.ok) {
                try {
                    const errorData = await res.json();
                    let errorMessage = errorData.detail || `HTTP ${res.status}`;

                    // Provide user-friendly error messages
                    if (res.status === 409) {
                        const detail = typeof errorData.detail === "string" ? errorData.detail : "";
                        const guardianConflict = [data.parentEmail, data.parentEmail2]
                            .filter(Boolean)
                            .some((email) => detail.toLowerCase().includes(String(email).toLowerCase()));
                        if (guardianConflict || /parent\/guardian/i.test(detail)) {
                            errorMessage = "That parent/guardian email is already used by another account. Please use a different email.";
                        } else {
                            errorMessage = `An athlete with email ${data.email} already exists. Please use a different email.`;
                        }
                    } else if (res.status === 404) {
                        errorMessage = "Athlete not found. They may have been deleted.";
                    } else if (res.status === 403) {
                        errorMessage = "You do not have permission to update this athlete.";
                    }

                    setServerError(errorMessage);
                    return;
                } catch (jsonError) {
                    setServerError(`An unexpected error occurred.`);
                    return;
                }
            }

            // Capture the athlete ID from the response
            let savedData = data;
            let saveMeta: AthleteSaveMeta | undefined;

            const contentType = res.headers.get("content-type");

            if (res.status !== 204 && contentType?.includes("application/json")) {
                try {
                    const text = await res.text();
                    if (text && text.trim().length > 0) {
                        const responseData = JSON.parse(text);
                        if (responseData.id != null) {
                            savedData = { ...data, id: responseData.id };
                            if (isAthlete()) {
                                localStorage.setItem("athleteId", String(responseData.id));
                            }
                        }
                        if (isCompanyAthleteResponseBody(responseData)) {
                            saveMeta = { companyAthlete: responseData };
                        }
                    }
                } catch {
                    // Empty or non-JSON body is acceptable for successful save
                }
            }

            setOk("Athlete profile saved.");
            onSaved?.(savedData, saveMeta);

            if (afterSaveTo) {
                const target = typeof afterSaveTo === "function" ? afterSaveTo(savedData) : afterSaveTo;
                if (target) {
                    navigate(target);
                    return;
                }
            }
            onClose?.();
        } catch (e: any) {
            setServerError(e?.message || "Failed to save.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className="card form-card profile-card" aria-busy={loading}>
            <div className="form-head">
                <h2 className="form-title">{data?.id != null ? "Edit athlete" : "Add athlete"}</h2>
                {!data?.id && (
                    <p className="form-subtitle">
                        The student-athlete and parent/guardian(s) will receive an email immediately after you select "Add Athlete" to access the account. Please be sure they check their spam or junk folder.
                    </p>
                )}
            </div>

            {(serverError || ok) && (
                <div className={`form-alert ${serverError ? "is-error" : "is-ok"}`}>{serverError || ok}</div>
            )}

            <div className="grid-2">
                <div className="field">
                    <label>First name*</label>
                    <input
                        className={`input${fieldErrors.firstName ? " input-error" : ""}`}
                        value={data.firstName}
                        onChange={(e) => { set("firstName")(e); setFieldErrors((fe) => ({ ...fe, firstName: undefined })); }}
                        data-testid="athlete-first-name-input"
                    />
                    {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
                </div>

                <div className="field">
                    <label>Last name*</label>
                    <input
                        className={`input${fieldErrors.lastName ? " input-error" : ""}`}
                        value={data.lastName}
                        onChange={(e) => { set("lastName")(e); setFieldErrors((fe) => ({ ...fe, lastName: undefined })); }}
                        data-testid="athlete-last-name-input"
                    />
                    {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
                </div>

                <div className="field">
                    <label>Student email*</label>
                    <input
                        className={`input${fieldErrors.email ? " input-error" : ""}`}
                        type="email"
                        value={data.email}
                        onChange={(e) => { set("email")(e); setFieldErrors((fe) => ({ ...fe, email: undefined })); }}
                        data-testid="athlete-email-input"
                        disabled={!!data.id}
                        readOnly={!!data.id}
                    />
                    {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                </div>

                {!isCleverUser && (
                <div className="field">
                    <label>Parent/Guardian 1 email*</label>
                    <input
                        className={`input${fieldErrors.parentEmail ? " input-error" : ""}`}
                        type="email"
                        value={data.parentEmail}
                        onChange={(e) => { set("parentEmail")(e); setFieldErrors((fe) => ({ ...fe, parentEmail: undefined })); }}
                        data-testid="athlete-parent-email-input"
                        disabled={parentEmailLocked}
                        readOnly={parentEmailLocked}
                    />
                    {fieldErrors.parentEmail && <span className="field-error">{fieldErrors.parentEmail}</span>}
                </div>
                )}

                {!isCleverUser && (
                <div className="field">
                    <label>Parent/Guardian 2 email</label>
                    <input
                        className={`input${fieldErrors.parentEmail2 ? " input-error" : ""}`}
                        type="email"
                        value={data.parentEmail2 || ""}
                        onChange={(e) => { set("parentEmail2")(e); setFieldErrors((fe) => ({ ...fe, parentEmail2: undefined })); }}
                        data-testid="athlete-parent-email-2-input"
                        disabled={parentEmail2Locked}
                        readOnly={parentEmail2Locked}
                        placeholder="Optional"
                    />
                    {fieldErrors.parentEmail2 && <span className="field-error">{fieldErrors.parentEmail2}</span>}
                </div>
                )}

                <ComboBox
                    label="Team lead"
                    value={data.staffId}
                    options={staffOptions}
                    onChange={(id) => {
                        if (!id) {
                            setData((s) => ({ ...s, staffId: undefined, staffName: "" }));
                            return;
                        }
                        const found = staffOptions.find((o) => o.id === id);
                        setData((s) => ({
                            ...s,
                            staffName: found?.name || s.staffName,
                            staffId: found?.id,
                        }));
                    }}
                    disabled={staffOptions.length === 0 || loading}
                    loading={loading}
                    placeholder="Search for a team lead..."
                    testId="athlete-staff-select"
                    noOptionsMessage="No staff available"
                    loadingMessage="Loading team leads..."
                />

                <ComboBox
                    label="Sport"
                    value={data.sport?.id}
                    options={sports}
                    groupedOptions={sportGroups.length > 0 ? sportGroups : undefined}
                    onChange={(id) => {
                        if (!id) {
                            setData((s) => ({ ...s, sport: undefined, sportName: undefined }));
                            return;
                        }
                        const selectedSport = sports.find((sport) => sport.id === id);
                        if (selectedSport) {
                            setData((s) => ({ ...s, sport: selectedSport, sportName: selectedSport.name }));
                        }
                    }}
                    disabled={sportsLoading}
                    loading={sportsLoading}
                    error={sportsError ? "Failed to load sports" : undefined}
                    placeholder="Search for a sport..."
                    testId="athlete-sport-input"
                    noOptionsMessage="No sports found"
                    loadingMessage="Loading sports..."
                />

                {/* Form Actions Row - moved to bottom */}
                <div className="form-actions-row">
                    {/* Show delete link only when editing an existing athlete */}
                    {data.id ? (
                        <button
                            type="button"
                            className="link-danger"
                            onClick={() => setShowDeleteModal(true)}
                            data-testid="athlete-delete-link"
                        >
                            DELETE ATHLETE
                        </button>
                    ) : (
                        <div></div>
                    )}
                    <div className="actions-right">
                        <button type="button" className="btn-cancel" onClick={onClose} data-testid="athlete-cancel-btn">
                            Cancel
                        </button>
                        <button
                            className="btn-pill"
                            onClick={onSave}
                            disabled={saving || loading}
                            aria-busy={saving}
                            data-testid="athlete-save-btn"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteAthleteModal
                open={showDeleteModal}
                athleteName={`${data.firstName} ${data.lastName}`}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                loading={saving}
            />
        </section>
    );
}